import React, { ReactNode } from "react";
import "./HoverRevealActions.css";

/**
 * Put this on the row element that owns the actions (usually the `<tr>`).
 * It is the hook the reveal selector scopes to, so the actions of one row
 * cannot be revealed by hovering another.
 */
export const hoverRevealHostClass = "HoverRevealActionsHost";

export interface HoverRevealActionsProps {
    children: ReactNode;
    id?: string;
    /** Extra classes for the wrapper, e.g. the consuming table's own layout rules. */
    className?: string;
    /** Accessible name for the action group. */
    label?: string;
}

/**
 * Hides a row's actions until the row is hovered, or until something inside them
 * takes keyboard focus. Keeps a table from reading as a wall of icons without
 * making the actions mouse-only.
 *
 * The reveal is pure CSS, so hovering does not re-render the table. That matters
 * for long or lazy-loaded grids, where hover state in React would re-render every
 * row on each pointer move.
 *
 * @example
 * <tr className={`MyRow ${hoverRevealHostClass}`}>
 *     <td>
 *         <HoverRevealActions id={`row-menu-${row.id}`}>
 *             <TableRowMenu items={items} />
 *         </HoverRevealActions>
 *     </td>
 * </tr>
 */
const HoverRevealActions: React.FC<HoverRevealActionsProps> = ({
    children,
    id,
    className,
    label = "Row actions",
}) => (
    // biome-ignore lint/a11y/useSemanticElements: <fieldset> is not valid inside a <td> and brings its own layout; role="group" is the right semantic for an action cluster
    <div
        id={id}
        className={`HoverRevealActions ${className ?? ""}`.trim()}
        role="group"
        aria-label={label}
    >
        {children}
    </div>
);

export default HoverRevealActions;
